import React,{useState} from 'react';
import {motion} from 'framer-motion';
import AddressInput from './components/AddressInput';
import MapDisplay from './components/MapDisplay';
import ResultsPanel from './components/ResultsPanel';
import Header from './components/Header';
import LoadingAnimation from './components/LoadingAnimation';
import './App.css';

function App() {
  const [originalLocation, setOriginalLocation] = useState(null);
  const [antipodeLocation, setAntipodeLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLocationFound = (original, antipode) => {
    setOriginalLocation(original);
    setAntipodeLocation(antipode);
    setError(null);
  };

  const handleLoading = (loading) => {
    setIsLoading(loading);
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);
    setIsLoading(false);
  };

  const handleReset = () => {
    setOriginalLocation(null);
    setAntipodeLocation(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
      <div className="relative">
        {/* Animated background particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-blue-400 rounded-full opacity-30"
              animate={{
                x: [0, 100, 0],
                y: [0, -100, 0],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 3 + Math.random() * 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>

        <Header />

        <main className="relative z-10 container mx-auto px-4 py-8">
          <div className="max-w-7xl mx-auto">
            {/* Branding Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-8"
            >
              <p className="text-slate-400 mb-2">Brought to you by:</p>
              <motion.a
                href="https://strikehold.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block"
              >
                <img 
                  src="https://i.imghippo.com/files/suN9048w.png" 
                  alt="Strike Hold Logo" 
                  className="w-56 mx-auto cursor-pointer hover:opacity-80 transition-opacity duration-300"
                />
              </motion.a>
            </motion.div>

            {/* Search Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <AddressInput
                onLocationFound={handleLocationFound}
                onLoading={handleLoading}
                onError={handleError}
                onReset={handleReset}
              />
            </motion.div>

            {/* Loading Animation */}
            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mb-8"
              >
                <LoadingAnimation />
              </motion.div>
            )}

            {/* Error Display */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8 bg-red-500/20 border border-red-500/30 rounded-xl p-6 text-center"
              >
                <p className="text-red-300 text-lg">{error}</p>
              </motion.div>
            )}

            {/* Results Section */}
            {originalLocation && antipodeLocation && !isLoading && (
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="space-y-8"
              >
                <ResultsPanel
                  originalLocation={originalLocation}
                  antipodeLocation={antipodeLocation}
                />
                <MapDisplay
                  originalLocation={originalLocation}
                  antipodeLocation={antipodeLocation}
                />
              </motion.div>
            )}

            {/* Instructions */}
            {!originalLocation && !isLoading && !error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-center mt-16"
              >
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 max-w-2xl mx-auto">
                  <h3 className="text-2xl font-semibold text-white mb-4">
                    How it works
                  </h3>
                  <div className="text-slate-300 space-y-3 text-left">
                    <p>• Enter any address on Earth</p>
                    <p>• We'll calculate the exact opposite point (antipode)</p>
                    <p>• See both locations on an interactive map</p>
                    <p>• Discover fascinating geographical facts!</p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;